import React, { useState, useEffect } from 'react';
import {
  Plus, ArrowLeft, X, Eye, Save, HelpCircle, User, Search,
  Globe, Shield, ChevronDown, ArrowUp, ThumbsUp, ThumbsDown,
  Share2, FileText, Flag, Reply, ChevronRight, Send, Star,
  MessageSquare, Calendar, Users, Check, LogIn, Pencil, Trash2,
  Bell, Settings, MapPin, Image, Heart, Bookmark, ExternalLink,
  AlertTriangle, Info, RefreshCw, Upload, Download, Lock, Unlock,
  Home, Menu, MoreHorizontal, Copy, Ban
} from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import { tokens, variantMap, getSizing, getContextColor } from '../../styles/designTokens';
import ConfirmationModal from './ConfirmationModal';

// ---------------------------------------------------------------------------
// Icon registry — all Lucide icons available to StandardButton via string name
// To add a new icon: import it above and add it here
// ---------------------------------------------------------------------------
const iconRegistry = {
  Plus, ArrowLeft, X, Eye, Save, HelpCircle, User, Search,
  Globe, Shield, ChevronDown, ArrowUp, ThumbsUp, ThumbsDown,
  Share2, FileText, Flag, Reply, ChevronRight, Send, Star,
  MessageSquare, Calendar, Users, Check, LogIn, Pencil, Trash2,
  Bell, Settings, MapPin, Image, Heart, Bookmark, ExternalLink,
  AlertTriangle, Info, RefreshCw, Upload, Download, Lock, Unlock,
  Home, Menu, MoreHorizontal, Copy, Ban
};

// ---------------------------------------------------------------------------
// Scroll threshold for showWhen='scrolled' and 'scrolled-and-has-more'
// ---------------------------------------------------------------------------
const SCROLL_THRESHOLD = 200;

// ===========================================================================
// StandardButton
//
// The single button component for the entire OurNet platform.
// Every button on every page should be a StandardButton instance.
//
// TWO APIs — both are supported:
//
// LEGACY API (backwards compatible — existing footers still use this):
//   variant = 'icon' | 'tiny' | 'small' | 'medium'
//   text    = "Label"
//   color   = "#hex"   (optional)
//
// SEMANTIC API (new — use this for all new buttons):
//   variant = 'context' | 'action' | 'destructive' | 'utility' |
//             'identity' | 'ghost' | 'ghost-action' | 'inline' | 'admin'
//   iconName = "Plus"   (Lucide icon name string)
//   label    = "Create" (text label — optional, omit for circle-only)
//
// See docs/HeadersFootersButtons/button-design-system-brief.md for full spec
// ===========================================================================

const LEGACY_VARIANTS = new Set(['icon', 'tiny', 'small', 'medium']);

const StandardButton = ({
  // ── Shared props ──────────────────────────────────────────────────────────
  variant   = 'small',   // semantic or legacy variant name
  onClick   = () => {},
  disabled  = false,
  title     = '',        // tooltip

  // ── Legacy API ────────────────────────────────────────────────────────────
  text      = '',        // button label (legacy)
  icon      = null,      // icon ReactNode (legacy, not an icon name string)
  color     = null,      // explicit hex color override (legacy)
  style     = {},        // style override (legacy)

  // ── Semantic API ─────────────────────────────────────────────────────────
  iconName      = null,   // Lucide icon name string, e.g. 'Plus', 'ArrowLeft'
  label         = null,   // text label (null = circle/icon-only button)
  size          = null,   // override shape: 'circle' | 'pill' | 'ghost' (auto-detected if null)
  profileImage  = null,   // URL — identity variant only: shows photo with orange ring
  showWhen      = 'always', // 'always' | 'scrolled' | 'has-more' | 'scrolled-and-has-more'
  hasMore       = true,   // external flag for 'has-more' / 'scrolled-and-has-more'
  inlineTextColor = null, // inline variant only — explicit colour overriding the variant's
                           // default textColor token (tokens.colors.inlineText, currently an
                           // undefined CSS custom property — see docs/reuse-backlog.md 2026-08-14).
                           // For a control mounted before the ambient theme has switched context
                           // (e.g. a Groups-coloured control on the still-locality front page),
                           // where useTheme()/variant="context" would resolve the wrong colour.
                           // null preserves today's default resolution untouched.
  confirmBefore = null,   // true = always confirm; false = never; null = use variant default
  checkUnsaved  = null,   // true = check before navigating; null = use variant default
  onCheckUnsaved = null,  // () => boolean | Promise<boolean>  — called when checkUnsaved active

  // ── Pill-branch-only sizing overrides ────────────────────────────────────
  // Opt-in, default-null — every other shape/variant is untouched. Added for
  // a narrow, height-matched, stacked-label destructive pill (see
  // docs/briefs/2026-08-24-selector-chrome-layout-limpar-tudo.md); the pill
  // branch is otherwise fully token-driven with no override hook. `style`
  // (legacy prop, above) has no effect on the pill branch — use these
  // instead, not style={{...}}.
  heightOverride = null,  // number (px) — replaces the token height (pill branch only)
  wrapLabel      = false, // true = whiteSpace 'normal' + centred label, so a two-word label
                           // wraps at its natural word-space instead of forcing the button
                           // wide. Requires `width` (text can't wrap without a constraint).
                           // Size `width` for the LONGEST expected locale string, not just
                           // the English source — this clips (via ellipsis) rather than
                           // reflowing if a translation runs long.
  width          = null,  // number (px) — fixes the pill's width instead of growing from
                           // label content (pill branch only; required when wrapLabel=true)

  // ── Confirmation modal customisation ─────────────────────────────────────
  confirmTitle   = 'Are you sure?',
  confirmMessage = 'This action cannot be undone.',
  confirmOkLabel = 'Continue',
}) => {
  const theme = useTheme();
  const { isMobile } = useMediaQuery();

  // Confirmation modal state
  const [showConfirm, setShowConfirm] = useState(false);
  const [pendingClick, setPendingClick] = useState(null);

  // Hover state for legacy path (must be unconditional — cannot be inside if block)
  const [isLegacyHovered, setIsLegacyHovered] = useState(false);

  // Scroll tracking (for showWhen='scrolled' and 'scrolled-and-has-more')
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    if (showWhen === 'always' || showWhen === 'has-more') return;
    const handleScroll = () => setIsScrolled(window.scrollY > SCROLL_THRESHOLD);
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll(); // initial check
    return () => window.removeEventListener('scroll', handleScroll);
  }, [showWhen]);

  // ── Conditional visibility ────────────────────────────────────────────────
  const isVisible = (() => {
    switch (showWhen) {
      case 'scrolled':              return isScrolled;
      case 'has-more':              return hasMore;
      case 'scrolled-and-has-more': return isScrolled && hasMore;
      default:                      return true;
    }
  })();
  if (!isVisible) return null;

  // =========================================================================
  // LEGACY PATH — variant is 'icon' | 'tiny' | 'small' | 'medium'
  // =========================================================================
  if (LEGACY_VARIANTS.has(variant)) {
    const buttonColor = color || theme?.primary || '#4ECDC4';
    const dimensionMap = {
      icon:   { width: '50px', height: '50px',  borderRadius: '25px', fontSize: '20px', fontWeight: '700' },
      tiny:   { width: '70px', height: '40px',  borderRadius: '20px', fontSize: '13px', fontWeight: '600' },
      small:  { width: '100px', height: '50px', borderRadius: '25px', fontSize: '15px', fontWeight: '600' },
      medium: { width: '190px', height: '50px', borderRadius: '25px', fontSize: '15px', fontWeight: '600' },
    };
    const dims = dimensionMap[variant];

    return (
      <button
        style={{
          ...dims,
          background:     disabled ? tokens.colors.disabledBackground : buttonColor,
          color:          disabled ? tokens.colors.disabledText : 'white',
          border:         'none',
          cursor:         disabled ? 'not-allowed' : 'pointer',
          display:        'flex',
          alignItems:     'center',
          justifyContent: 'center',
          transition:     'opacity 0.2s',
          opacity:        disabled ? 0.5 : (isLegacyHovered ? 0.85 : 1),
          fontFamily:     tokens.typography.fontFamily,
          ...style,
        }}
        onClick={disabled ? undefined : onClick}
        onMouseEnter={() => setIsLegacyHovered(true)}
        onMouseLeave={() => setIsLegacyHovered(false)}
        disabled={disabled}
        title={title}
      >
        {icon || text}
      </button>
    );
  }

  // =========================================================================
  // SEMANTIC PATH — variant is one of the design-system semantic names
  // =========================================================================

  // Resolve variant config
  const variantConfig = variantMap[variant] ?? variantMap.action;

  // Resolve shape: explicit size prop > variant default
  const resolvedShape = size ?? variantConfig.shape ?? 'pill';

  // Resolve colours
  const resolvedBg = (() => {
    if (variant === 'context') return getContextColor(theme);
    if (resolvedShape === 'inline' || resolvedShape === 'inline-emphasis') return 'transparent';
    return variantConfig.color ?? tokens.colors.action;
  })();
  const resolvedTextColor = variantConfig.textColor ?? '#FFFFFF';
  const resolvedBorderColor = variantConfig.borderColor ?? null;

  // Resolve sizing from tokens
  const sz = getSizing(resolvedShape, isMobile);

  // Resolve behaviour defaults
  const shouldConfirm = confirmBefore !== null
    ? confirmBefore
    : (variantConfig.confirmBefore ?? false);

  const shouldCheckUnsaved = checkUnsaved !== null
    ? checkUnsaved
    : (variantConfig.checkUnsaved ?? false);

  // Resolve icon component
  const IconComponent = iconName ? (iconRegistry[iconName] ?? null) : null;

  // ── Click handler with behaviour contracts ─────────────────────────────
  const handleClick = async (e) => {
    if (disabled) return;

    // 1. Check for unsaved changes (context/back buttons by default)
    if (shouldCheckUnsaved && onCheckUnsaved) {
      const canProceed = await Promise.resolve(onCheckUnsaved());
      if (!canProceed) return;
    }

    // 2. Confirmation modal (destructive actions by default)
    if (shouldConfirm) {
      setPendingClick(() => () => onClick(e));
      setShowConfirm(true);
      return;
    }

    onClick(e);
  };

  const handleConfirmOk = () => {
    setShowConfirm(false);
    if (pendingClick) { pendingClick(); setPendingClick(null); }
  };

  const handleConfirmCancel = () => {
    setShowConfirm(false);
    setPendingClick(null);
  };

  // ── Build shared interactive styles ──────────────────────────────────────
  const isDestructive = variant === 'destructive';

  // =========================================================================
  // INLINE variant (and its bolder sibling, inline-emphasis) — bare icon + text, no
  // container. Same layout/behaviour, own sizing tier per shape via getSizing.
  // =========================================================================
  if (resolvedShape === 'inline' || resolvedShape === 'inline-emphasis') {
    const inlineSz = getSizing(resolvedShape, isMobile);
    // inlineTextColor, when supplied, overrides the variant's default textColor for this
    // instance only — see the prop's own doc comment above for why.
    const resolvedInlineColor = inlineTextColor ?? resolvedTextColor;
    return (
      <button
        title={title}
        disabled={disabled}
        onClick={handleClick}
        style={{
          display:        'flex',
          alignItems:     'center',
          gap:            `${inlineSz.gap}px`,
          background:     'transparent',
          border:         'none',
          padding:        0,
          cursor:         disabled ? 'not-allowed' : 'pointer',
          color:          disabled ? tokens.colors.disabledText : resolvedInlineColor,
          fontSize:       `${inlineSz.fontSize}px`,
          fontWeight:     inlineSz.fontWeight,
          fontFamily:     tokens.typography.fontFamily,
          opacity:        disabled ? 0.5 : 1,
          transition:     'opacity 0.15s',
          lineHeight:     1,
        }}
        onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.7'; }}
        onMouseLeave={e => { if (!disabled) e.currentTarget.style.opacity = '1'; }}
      >
        {IconComponent && (
          <IconComponent
            size={inlineSz.iconSize}
            strokeWidth={tokens.icon.strokeWidth}
            color={disabled ? tokens.colors.disabledText : resolvedInlineColor}
          />
        )}
        {label && <span>{label}</span>}
      </button>
    );
  }

  // =========================================================================
  // CIRCLE variant — identity (Profile/Help/Log In) and admin
  // =========================================================================
  if (resolvedShape === 'circle') {
    const circleSz = getSizing('circle', isMobile);
    const hasProfileImage = variant === 'identity' && profileImage;

    return (
      <>
        <button
          title={title}
          disabled={disabled}
          onClick={handleClick}
          style={{
            width:          `${circleSz.diameter}px`,
            height:         `${circleSz.diameter}px`,
            borderRadius:   '50%',
            background:     disabled ? tokens.colors.disabledBackground : resolvedBg,
            border:         hasProfileImage ? `3px solid ${tokens.colors.identity}` : 'none',
            cursor:         disabled ? 'not-allowed' : 'pointer',
            display:        'flex',
            alignItems:     'center',
            justifyContent: 'center',
            overflow:       'hidden',
            padding:        0,
            flexShrink:     0,
            opacity:        disabled ? 0.5 : 1,
            transition:     'opacity 0.15s, transform 0.1s',
          }}
          onMouseEnter={e => { if (!disabled) { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.05)'; } }}
          onMouseLeave={e => { if (!disabled) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; } }}
        >
          {hasProfileImage ? (
            <img
              src={profileImage}
              alt="Profile"
              style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
            />
          ) : (
            IconComponent && (
              <IconComponent
                size={circleSz.iconSize}
                strokeWidth={tokens.icon.strokeWidth}
                color={disabled ? tokens.colors.disabledText : resolvedTextColor}
              />
            )
          )}
        </button>

        {shouldConfirm && (
          <ConfirmationModal
            visible={showConfirm}
            title={confirmTitle}
            message={confirmMessage}
            okLabel={confirmOkLabel}
            cancelLabel="Cancel"
            danger={isDestructive}
            onOk={handleConfirmOk}
            onCancel={handleConfirmCancel}
          />
        )}
      </>
    );
  }

  // =========================================================================
  // GHOST variant — dilute teal background with border (Share button)
  // =========================================================================
  if (resolvedShape === 'ghost') {
    const ghostSz = getSizing('ghost', isMobile);
    return (
      <>
        <button
          title={title}
          disabled={disabled}
          onClick={handleClick}
          style={{
            height:         `${ghostSz.height}px`,
            paddingLeft:    `${ghostSz.paddingH}px`,
            paddingRight:   `${ghostSz.paddingH}px`,
            borderRadius:   `${ghostSz.borderRadius}px`,
            background:     disabled ? tokens.colors.disabledBackground : resolvedBg,
            border:         `1px solid ${resolvedBorderColor ?? tokens.colors.borderSecondary}`,
            cursor:         disabled ? 'not-allowed' : 'pointer',
            display:        'flex',
            alignItems:     'center',
            gap:            `${ghostSz.gap}px`,
            color:          disabled ? tokens.colors.disabledText : resolvedTextColor,
            fontSize:       `${ghostSz.fontSize}px`,
            fontWeight:     ghostSz.fontWeight,
            fontFamily:     tokens.typography.fontFamily,
            letterSpacing:  tokens.typography.buttonLetterSpacing,
            opacity:        disabled ? 0.5 : 1,
            transition:     'opacity 0.15s',
          }}
          onMouseEnter={e => { if (!disabled) e.currentTarget.style.opacity = '0.8'; }}
          onMouseLeave={e => { if (!disabled) e.currentTarget.style.opacity = '1'; }}
        >
          {IconComponent && (
            <IconComponent
              size={ghostSz.iconSize}
              strokeWidth={tokens.icon.strokeWidth}
              color={disabled ? tokens.colors.disabledText : resolvedTextColor}
            />
          )}
          {label && <span>{label}</span>}
        </button>

        {shouldConfirm && (
          <ConfirmationModal
            visible={showConfirm}
            title={confirmTitle}
            message={confirmMessage}
            okLabel={confirmOkLabel}
            cancelLabel="Cancel"
            danger={isDestructive}
            onOk={handleConfirmOk}
            onCancel={handleConfirmCancel}
          />
        )}
      </>
    );
  }

  // =========================================================================
  // PILL variant — default shape for most action buttons
  // =========================================================================
  const pillSz = getSizing('pill', isMobile);

  return (
    <>
      <button
        title={title}
        disabled={disabled}
        onClick={handleClick}
        style={{
          height:         `${heightOverride ?? pillSz.height}px`,
          paddingLeft:    `${pillSz.paddingH}px`,
          paddingRight:   `${pillSz.paddingH}px`,
          borderRadius:   `${pillSz.borderRadius}px`,
          background:     disabled ? tokens.colors.disabledBackground : resolvedBg,
          border:         'none',
          cursor:         disabled ? 'not-allowed' : 'pointer',
          display:        'flex',
          alignItems:     'center',
          justifyContent: width != null ? 'center' : 'flex-start',
          gap:            `${pillSz.gap}px`,
          color:          disabled ? tokens.colors.disabledText : resolvedTextColor,
          fontSize:       `${pillSz.fontSize}px`,
          fontWeight:     pillSz.fontWeight,
          fontFamily:     tokens.typography.fontFamily,
          letterSpacing:  tokens.typography.buttonLetterSpacing,
          whiteSpace:     wrapLabel ? 'normal' : 'nowrap',
          opacity:        disabled ? 0.5 : 1,
          transition:     'opacity 0.15s, transform 0.1s',
          flexShrink:     0,
          ...(width != null ? { width: `${width}px`, overflow: 'hidden' } : {}),
        }}
        onMouseEnter={e => { if (!disabled) { e.currentTarget.style.opacity = '0.85'; e.currentTarget.style.transform = 'scale(1.02)'; } }}
        onMouseLeave={e => { if (!disabled) { e.currentTarget.style.opacity = '1'; e.currentTarget.style.transform = 'scale(1)'; } }}
      >
        {IconComponent && (
          <IconComponent
            size={pillSz.iconSize}
            strokeWidth={tokens.icon.strokeWidth}
            color={disabled ? tokens.colors.disabledText : resolvedTextColor}
          />
        )}
        {label && (
          <span style={wrapLabel ? { textAlign: 'center', width: '100%' } : undefined}>
            {label}
          </span>
        )}
      </button>

      {shouldConfirm && (
        <ConfirmationModal
          visible={showConfirm}
          title={confirmTitle}
          message={confirmMessage}
          okLabel={confirmOkLabel}
          cancelLabel="Cancel"
          danger={isDestructive}
          onOk={handleConfirmOk}
          onCancel={handleConfirmCancel}
        />
      )}
    </>
  );
};

export default StandardButton;
